import {Money} from '@dintero/money';
import type {ValueOf} from 'type-fest';

import type {Image} from '@/modules/foundation/components/image/types';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import type {ProductFilters} from '../filters/query';
import type {ItemForPriceFragment, ItemWithVatTypeFragment} from '../item/query';
import {getItemQuantityPriceWithVat} from '../item/utils';

import {
	type CabFloorMatConfiguration,
	cabFloorMatProductAdapter,
} from './cab-floor-mat/configuration';
import {DOCUMENT_TYPE_CAB_FLOOR_MAT_PRODUCT} from './cab-floor-mat/schema/names';
import {
	type CabShoeTrayConfiguration,
	cabShoeTrayProductAdapter,
} from './cab-shoe-tray/configuration';
import {DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT} from './cab-shoe-tray/schema/names';
import {type CabTableConfiguration, cabTableProductAdapter} from './cab-table/configuration';
import {DOCUMENT_TYPE_CAB_TABLE_PRODDUCT} from './cab-table/schema/names';
import {
	type ClothingConfiguration,
	clothingProductAdapter,
	customClothingProductAdapter,
} from './clothing/configuration';
import {
	DOCUMENT_TYPE_CLOTHING_PRODUCT,
	DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT,
} from './clothing/schema/names';
import {productAdapter, type ProductConfiguration} from './product/configuration';
import {DOCUMENT_TYPE_PRODUCT} from './product/schema/names';
import type {StickerProductConfiguration} from './stickers/configuration/stickerProductConfiguration';
import {stickerProductAdapter} from './stickers/configuration/stickerProductConfiguration';
import {DOCUMENT_TYPE_STICKER_PRODUCT} from './stickers/schema/names';
import type {CoreProductAdapter, ProductAdapter} from './adapter';
import type {ProductDescription} from './description';
import type {ProductImageSide} from './ProductImage';
import type {UnionProductCoreFragment, UnionProductFragment} from './query';

/**
 * A mapping from product type name to the corresponding configuration type.
 *
 * The config types of all product types that use the ProductAdapter must be registered here as well.
 */
type ProductConfigurationTypeByTypeName = {
	[DOCUMENT_TYPE_PRODUCT]: ProductConfiguration;
	[DOCUMENT_TYPE_CAB_FLOOR_MAT_PRODUCT]: CabFloorMatConfiguration;
	[DOCUMENT_TYPE_CAB_TABLE_PRODDUCT]: CabTableConfiguration;
	[DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT]: CabShoeTrayConfiguration;
	[DOCUMENT_TYPE_CLOTHING_PRODUCT]: ClothingConfiguration;
	[DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT]: ClothingConfiguration;
	[DOCUMENT_TYPE_STICKER_PRODUCT]: StickerProductConfiguration;
};

/**
 * The union of the configuration types for all supported product types.
 */
export type UnionProductConfiguration = ValueOf<ProductConfigurationTypeByTypeName>;

/**
 * Given a full or core product fragment type, returns the corresponding configuration type.
 */
export type ProductConfigTypeOf<P extends UnionProductCoreFragment> =
	ProductConfigurationTypeByTypeName[P['_type']];

/**
 * A record of type-erased adapter types, acting as interfaces. Using the correct _type key ensures type-safety.
 * Typescript needs an explicit cast on adapters, fragments and configurations otherwise.
 */
const adapters = {
	[DOCUMENT_TYPE_PRODUCT]: productAdapter,
	[DOCUMENT_TYPE_CAB_FLOOR_MAT_PRODUCT]: cabFloorMatProductAdapter,
	[DOCUMENT_TYPE_CAB_SHOE_TRAY_PRODDUCT]: cabShoeTrayProductAdapter,
	[DOCUMENT_TYPE_CAB_TABLE_PRODDUCT]: cabTableProductAdapter,
	[DOCUMENT_TYPE_CLOTHING_PRODUCT]: clothingProductAdapter,
	[DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT]: customClothingProductAdapter,
	[DOCUMENT_TYPE_STICKER_PRODUCT]: stickerProductAdapter,
};

export function getAdapter<P extends UnionProductFragment>(product: P) {
	// FIXME: This cast through unknown means we have no real type safety to ensure all product
	//        types have properly typed product adapters.
	//
	//        This may be possible to fix somehow, but I've exhausted my type-fu skills trying
	//        to find a good solution. I need to move on to something else for now.
	return adapters[product._type] as unknown as ProductAdapter<P, P>;
}

/**
 * Returns the part of the product adapter that also works for products loaded with the core
 * product fragment.
 */
function getCoreAdapter<C extends UnionProductCoreFragment>(product: C) {
	// FIXME: See getAdapter.
	return adapters[product._type] as unknown as CoreProductAdapter<C>;
}

/**
 * Parses a product configuration from a JSON object.
 *
 * Returns the parsed configuration typed according to the given product fragment.
 */
export function parseProductConfiguration<P extends UnionProductFragment>(
	product: P,
	json: unknown,
) {
	const config = getAdapter(product).parseConfigFromJSON(json);

	return config;
}

/**
 * Encodes a product configuration into URL search params.
 */
export function searchParamsFromProductConfiguration(configuration: UnionProductConfiguration) {
	const params = new URLSearchParams();

	params.append('configuration', btoa(JSON.stringify(configuration)));

	return params;
}

/**
 * Decodes a product configuration from URL search params.
 *
 * Returns the parsed configuration typed according to the given product fragment, or null if no configuration is found in the search params.
 */
export function productConfigurationFromSearchParams<P extends UnionProductFragment>(
	product: P,
	searchParams: URLSearchParams,
) {
	const param = searchParams.get('configuration');
	if (!param) {
		return null;
	}

	try {
		const jsonConfig = JSON.parse(atob(param));
		return parseProductConfiguration(product, jsonConfig);
	} catch (e) {
		console.warn('Failed to parse product configuration from search params', e);
		return null;
	}
}

/**
 * Decodes a product configuration from a FormData object.
 */
export function productConfigurationFromFormData<P extends UnionProductFragment>(
	product: P,
	formData: FormData,
) {
	return getAdapter(product).parseConfigFromFormData(product, formData);
}

/**
 * Validates that a product configuraion is valid for a given product.
 */
export function isValidConfiguration<P extends UnionProductFragment>(
	product: P,
	rawConfig: ProductConfigTypeOf<P>,
) {
	return getAdapter(product).isValidConfig(product, rawConfig);
}

/**
 *  Returns the items that are a part of a given product configuration.
 *
 *  Products loaded with the full product fragment resolve all item fields, while products loaded
 *  with the core fragment only resolve the fields needed to calculate prices.
 */
export function getItemsForConfiguration<P extends UnionProductFragment>(
	product: P,
	rawConfiguration: ProductConfigTypeOf<P>,
): ItemWithVatTypeFragment[];
export function getItemsForConfiguration<C extends UnionProductCoreFragment>(
	product: C,
	rawConfiguration: ProductConfigTypeOf<C>,
): ItemForPriceFragment[];
export function getItemsForConfiguration<C extends UnionProductCoreFragment>(
	product: C,
	rawConfiguration: ProductConfigTypeOf<C>,
) {
	// The adapters only pick items out of the given product, so the items of a full product are
	// full items, even though the adapters are typed against the core fragment.
	return getCoreAdapter(product).getItems(product, rawConfiguration);
}

/**
 * Calculates the total price and amount VAT for a given product configuration and quantity.
 */
export function getQuantityPriceForConfiguration<P extends UnionProductFragment>(
	product: P,
	configuration: ProductConfigTypeOf<P>,
	quantity: number,
) {
	let priceIncVat = Money.fromFractionlessAmount(0, 'NOK');
	let priceVat = Money.fromFractionlessAmount(0, 'NOK');

	// Find selected items:
	const selectedItems = getItemsForConfiguration(product, configuration);

	// Each product is made up of multiple items which may have different VAT rates. Sum up the
	// price for each item in the product quantity and sum up the total price and VAT for the
	// product to get the total product quantity price.
	for (const item of selectedItems) {
		if (!item.resolvedVatType || typeof item.resolvedVatType.percentage !== 'number') {
			throw new Error(
				'Cannot calculate price for item without VAT type set or with invalid VAT percentage.',
			);
		}

		const quantityPrice = getItemQuantityPriceWithVat(item, item.resolvedVatType, quantity);

		priceIncVat = priceIncVat.add(quantityPrice.amountIncVat);
		priceVat = priceVat.add(quantityPrice.amountVat);
	}

	return {priceIncVat, priceVat};
}

/**
 * Calculates the total price and amount VAT for a single quantity of a given product configuration.
 */
export function getPriceForConfiguration<P extends UnionProductFragment>(
	product: P,
	configuration: ProductConfigTypeOf<P>,
) {
	return getQuantityPriceForConfiguration(product, configuration, 1);
}

/**
 * Generates a short product name based on the given configuration of the product.
 *
 * Useful for building a product name to use in product lists, e.g. in the shopping cart or order
 * summary.
 */
export function getProductNameForConfiguration<C extends UnionProductCoreFragment>(
	product: C,
	configuration: ProductConfigTypeOf<C>,
) {
	return getCoreAdapter(product).getName(product, configuration);
}

export function getProductDescriptionForConfiguration<C extends UnionProductCoreFragment>(
	product: C,
	configuration: ProductConfigTypeOf<C>,
): ProductDescription | null {
	const adapter = getCoreAdapter(product);
	return adapter.getDescription ? adapter.getDescription(product, configuration) : [];
}

/**
 * Generates a product image stack for the given side of a configured product.
 */
export function getImageStackForConfiguration<
	Side extends ProductImageSide,
	C extends UnionProductCoreFragment,
>(side: Side, product: C, rawConfig: ProductConfigTypeOf<C> | null, allowPlaceholders: boolean) {
	return getCoreAdapter(product).getImages(side, product, rawConfig, allowPlaceholders);
}

export function getEnvironmentImages(product: UnionProductFragment, aspectRatio?: number) {
	// TODO: This should go through the adapter, to allow products to store their environment images however they'd like:

	const environmentalImages: Image[] = [];

	if ('environmentImages' in product && product.environmentImages) {
		for (const environmentImage of product.environmentImages) {
			const image = imageFromSanityImage(environmentImage, aspectRatio);
			if (image) {
				environmentalImages.push(image);
			}
		}
	}

	return environmentalImages;
}

/**
 * Given a list of set filters; returns the ones that are a match for the given product and configuration.
 */
function getFiltersMatchingProduct<C extends UnionProductCoreFragment>(
	product: C,
	filters: ProductFilters,
) {
	return getCoreAdapter(product).getMatchingFilters(product, filters);
}

/**
 * Returns true if any of the selected filters match given product and configuration.
 */
export function isProductMatchForFilters(
	product: UnionProductCoreFragment,
	filters: ProductFilters,
) {
	const matches = getFiltersMatchingProduct(product, filters);
	return matches !== null;
}
